import React, { useEffect, useMemo, useRef, useState } from "react";
import ConfirmationModal from "ui-components/confirmation-modal/confirmationModal";
import "./closeDoorModal.scss";
import LabeledSelectInput from "ui-components/molecules/labeled-inputs/labeled-select-input/labeledSelectInput";
import { LabelModes } from "ui-components/molecules/labeled-inputs/labeledInput";
import { debounce } from "lodash";
import { useCloseDoor } from "hooks/useLoadboard";
import EquipmentService from "app/data/equipment/equipmentServices";
import { XGSSelectOption } from "ui-components/xgs-select/xgsSelect";
import { useCarrierDropdown } from "hooks/useCarrier";
import XGSIcons from "ui-components/icon/xgsIcons";
import XGSIcon from "ui-components/icon/xgsIcon";
import { toast } from "react-toastify";
import LabeledTextInput from "ui-components/molecules/labeled-inputs/labeled-text-input/labeledTextInput";

export interface CloseDoorModalProps {
    show: boolean;
    onClose: () => void;
    closeddoordata?: any;
}

const CloseDoorModal: React.FC<CloseDoorModalProps> = ({
    show,
    onClose,
    closeddoordata
}) => {
    const terminalNumber = closeddoordata?.originTerminalNumber != null
        ? Number(closeddoordata.originTerminalNumber)
        : undefined;

    const loadedWeight = Number(closeddoordata?.loadedWeight) || 0;
    const MAX_CAPACITY = 42000;

    const loadPercentage = Math.min(
        Math.round((loadedWeight / MAX_CAPACITY) * 100),
        100
    );

    const equipmentService = EquipmentService.getInstance();
    const initializedRef = useRef(false);

    const [carrierFormValue, setCarrierFormValue] = useState<XGSSelectOption | null>(null);
    const [carrierSearch, setCarrierSearch] = useState<string>("");
    const [trailerFormValue, setTrailerFormValue] = useState<XGSSelectOption | null>(null);
    const [sealNumberFormValue, setSealNumberFormValue] = useState<string>("");

    const { mutate: closeDoor, isLoading: isClosing } = useCloseDoor();
    const isSealNumberValid = sealNumberFormValue.trim().length > 0;

    const handleCloseDoor = () => {
        if (terminalNumber == null) {
            toast.error("Origin terminal number is required to close the door.");
            return;
        }
        closeDoor({
            doorNumber: closeddoordata?.doorNumber,
            terminalNumber,
            payload: closeDoorPayload
        }, {
            onSuccess: () => {
                toast.success("Door closed successfully.");
                onClose();
            },
            onError: (e: unknown) => {
                const msg =
                    (e as { message?: string })?.message ??
                    (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
                    (e as { data?: { detail?: string } })?.data?.detail ??
                    "Failed to close door.";
                toast.error(msg);
            }
        });
    };

    const closeDoorPayload = useMemo(() => {
        const trailerValue = trailerFormValue?.value ?? closeddoordata?.trailerNumber ?? closeddoordata?.trailer;
        const payload: Record<string, number | string | undefined> = {
            manifestNumber: closeddoordata?.manifestNumber != null ? Number(closeddoordata.manifestNumber) : undefined,
            trailerNumber: trailerValue != null && trailerValue !== "" && !Number.isNaN(Number(trailerValue)) ? Number(trailerValue) : undefined,
            carrierNumber: carrierFormValue?.value != null ? Number(carrierFormValue.value) : closeddoordata?.carrierNumber != null ? Number(closeddoordata.carrierNumber) : undefined,
            sealNumber: sealNumberFormValue && sealNumberFormValue.trim() !== "" ? sealNumberFormValue.trim() : undefined,
        };
        return Object.fromEntries(Object.entries(payload).filter(([, v]) => v !== undefined && v !== null));
    }, [
        closeddoordata?.manifestNumber,
        closeddoordata?.carrierNumber,
        closeddoordata?.trailerNumber,
        closeddoordata?.trailer,
        trailerFormValue?.value,
        carrierFormValue?.value,
        sealNumberFormValue
    ]);

    const {
        data: carrierDropdownData = [],
        isLoading: isCarrierLoading,
    } = useCarrierDropdown();

    const carrierOptions = useMemo<XGSSelectOption[]>(() => {
        return carrierDropdownData
            .filter(c => c?.carrierNumber && c?.carrierName)
            .map(c => ({
                value: String(c.carrierNumber),
                label: (
                    <span>
                        {c.carrierName}{" "}
                        <span className="text-light">{c.carrierNumber}</span>
                    </span>
                ),
                sortLabel: `${c.carrierName} ${c.carrierNumber}`,
            }))
            .sort((a, b) => a.sortLabel!.localeCompare(b.sortLabel!));
    }, [carrierDropdownData]);

    useEffect(() => {
        if (!show) {
            setCarrierFormValue(null);
            setTrailerFormValue(null);
            initializedRef.current = false;
            return;
        }

        if (initializedRef.current) return;

        console.log(closeddoordata);
        
        if (closeddoordata?.sealNumber) {
            setSealNumberFormValue(closeddoordata?.sealNumber ?? "");
        }

        if (closeddoordata) {
            if (closeddoordata.carrierNumber && carrierOptions.length) {
                const carrier = carrierOptions.find(
                    (o) => o.value === String(closeddoordata.carrierNumber)
                );
                setCarrierFormValue(carrier ?? null);
            }

            if (closeddoordata.trailerNumber) {
                setTrailerFormValue({
                    value: closeddoordata.trailerNumber,
                    label: closeddoordata.trailerNumber,
                });
            }

            initializedRef.current = true;
        }
    }, [show, closeddoordata, carrierOptions]);

    const loadTrailerOptions = React.useMemo(
        () => debounce((query: string, callback: (options: XGSSelectOption[]) => void) => {
            equipmentService.getEquipmentListQuery(query, 0)
                .then(response => {
                    const options = response.data?.content.map(e => ({
                        label: e.unitNumber || "",
                        value: e.unitNumber || ""
                    })) || [];
                    callback(options);
                })
                .catch(() => {
                    callback([]);
                });
        }, 300),
        [equipmentService]
    );

    return (
        <ConfirmationModal
            opened={show}
            header={
                <div className="xgs-close-door-modal__header-wrapper">
                    <div>
                        <div className="xgs-close-door-modal__header">Close Door?</div>
                        <p className="xgs-close-door-modal__warning">
                            Once the door is closed, loading will be marked as complete.
                            You won’t be able to edit door, driver, trailer, or freight details.
                        </p>
                    </div>
                    <div className="xgs-close-door-modal__close">
                        <XGSIcon
                            icon={XGSIcons.faTimes}
                            size="lg"
                            onClick={onClose}
                        />
                    </div>
                </div>
            }
            confirmButtonText="Close Door"
            cancelButtonText="Cancel"
            onCancel={onClose}
            onConfirm={handleCloseDoor}
            spinner={isClosing}
            disableConfirmButton={isClosing || terminalNumber == null || !isSealNumberValid}
        >
            <div className="xgs-close-door-modal">
                <div className="xgs-close-door-modal__summary">
                    <h3 className="xgs-close-door-modal__summary-title">Load Summary</h3>

                    <div className="xgs-close-door-modal__table">
                        <div className="xgs-close-door-modal__row">
                            <span>Manifest:</span>
                            <span className="value">{closeddoordata?.manifestNumber}</span>
                        </div>
                        <div className="xgs-close-door-modal__row">
                            <span>Door:</span>
                            <span className="value">{closeddoordata?.doorNumber}</span>
                        </div>
                        <div className="xgs-close-door-modal__row">
                            <span>Origin:</span>
                            <span className="value">{closeddoordata?.originTerminalAirportCode} - {closeddoordata?.originTerminal} {closeddoordata?.originTerminalNumber}</span>
                        </div>
                        <div className="xgs-close-door-modal__row">
                            <span>Destination:</span>
                            <span className="value">{closeddoordata?.destinationTerminalAirportCode} - {closeddoordata?.destinationTerminal} {closeddoordata?.destinationTerminalNumber}</span>
                        </div>
                        <div className="xgs-close-door-modal__row">
                            <span>Load %:</span>
                            <span className="value">{loadPercentage + "%"}</span>
                        </div>
                        <div className="xgs-close-door-modal__row">
                            <span>Pieces:</span>
                            <span className="value">{closeddoordata?.pieces ?? 0}</span>
                        </div>
                        <div className="xgs-close-door-modal__row">
                            <span>Weight:</span>
                            <span className="value">{closeddoordata?.weight ?? 0} lbs</span>
                        </div>
                    </div>

                    <div className="xgs-close-door-modal__fields">
                        <LabeledSelectInput
                            name="carrier"
                            label="Carrier"
                            labelMode={LabelModes.column}
                            isDisabled
                            isSearchable={false}
                            menuIsOpen={false}
                            placeholder="Select Carrier"
                            value={carrierFormValue}
                            options={carrierOptions}
                            isLoading={isCarrierLoading}
                            inputValue={carrierSearch}
                            onInputChange={setCarrierSearch}
                            onValueChange={onChangeCarrier => setCarrierFormValue(onChangeCarrier || null)}
                            className="xgs-dispatch-load-modal__field disabled-dropdown"
                        />

                        <LabeledSelectInput
                            name="trailer"
                            label="Trailer"
                            labelMode={LabelModes.column}
                            placeholder="Start Typing..."
                            value={trailerFormValue}
                            options={[]}
                            className="xgs-dispatch-load-modal__field"
                            onValueChange={onChangeTrailer => setTrailerFormValue(onChangeTrailer || null)}
                            loadOptions={loadTrailerOptions}
                            async
                        />

                        <LabeledTextInput
                            name="sealNumber"
                            label="Seal Number"
                            required
                            labelMode={LabelModes.column}
                            className="xgs-dispatch-load-modal__field"
                            type="text"
                            value={sealNumberFormValue}
                            onChange={(e) => setSealNumberFormValue(e.currentTarget.value)}
                            placeholder="Enter or edit seal number"
                        />

                    </div>
                </div>
            </div>
        </ConfirmationModal>
    );
};

export default CloseDoorModal;